/**
 * Tag Color Utility
 * Provides consistent colors for tags across all pages
 * Same tag content will always get the same color
 */

// Define semantic color mappings for common tags
const semanticColors = {
    // Status-based tags
    'rejected': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', hover: 'hover:bg-red-100', hoverBorder: 'hover:border-red-300' },
    'approved': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', hover: 'hover:bg-green-100', hoverBorder: 'hover:border-green-300' },
    'pending': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', hover: 'hover:bg-yellow-100', hoverBorder: 'hover:border-yellow-300' },
    'completed': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', hover: 'hover:bg-blue-100', hoverBorder: 'hover:border-blue-300' },
    'in_progress': { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', hover: 'hover:bg-indigo-100', hoverBorder: 'hover:border-indigo-300' },
    'expired': { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', hover: 'hover:bg-gray-100', hoverBorder: 'hover:border-gray-300' },
    
    // Interview-related tags
    'technical': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', hover: 'hover:bg-purple-100', hoverBorder: 'hover:border-purple-300' },
    'behavioral': { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', hover: 'hover:bg-pink-100', hoverBorder: 'hover:border-pink-300' },
    'final': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', hover: 'hover:bg-orange-100', hoverBorder: 'hover:border-orange-300' },
    'first_round': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', hover: 'hover:bg-teal-100', hoverBorder: 'hover:border-teal-300' },
    'second_round': { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', hover: 'hover:bg-cyan-100', hoverBorder: 'hover:border-cyan-300' },
    
    // Priority tags
    'high': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', hover: 'hover:bg-red-100', hoverBorder: 'hover:border-red-300' },
    'medium': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', hover: 'hover:bg-yellow-100', hoverBorder: 'hover:border-yellow-300' },
    'low': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', hover: 'hover:bg-green-100', hoverBorder: 'hover:border-green-300' },
    
    // Department tags
    'engineering': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', hover: 'hover:bg-blue-100', hoverBorder: 'hover:border-blue-300' },
    'design': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', hover: 'hover:bg-purple-100', hoverBorder: 'hover:border-purple-300' },
    'marketing': { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', hover: 'hover:bg-pink-100', hoverBorder: 'hover:border-pink-300' },
    'sales': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', hover: 'hover:bg-green-100', hoverBorder: 'hover:border-green-300' },
    'hr': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', hover: 'hover:bg-orange-100', hoverBorder: 'hover:border-orange-300' },
    
    // Experience level tags
    'senior': { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', hover: 'hover:bg-indigo-100', hoverBorder: 'hover:border-indigo-300' },
    'mid': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', hover: 'hover:bg-blue-100', hoverBorder: 'hover:border-blue-300' },
    'junior': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', hover: 'hover:bg-green-100', hoverBorder: 'hover:border-green-300' },
    'intern': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', hover: 'hover:bg-yellow-100', hoverBorder: 'hover:border-yellow-300' }
};

// Fallback colors for tags not in semantic mapping
const fallbackColors = [
    { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', hover: 'hover:bg-blue-100', hoverBorder: 'hover:border-blue-300' },
    { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', hover: 'hover:bg-green-100', hoverBorder: 'hover:border-green-300' },
    { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', hover: 'hover:bg-purple-100', hoverBorder: 'hover:border-purple-300' },
    { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', hover: 'hover:bg-orange-100', hoverBorder: 'hover:border-orange-300' },
    { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', hover: 'hover:bg-pink-100', hoverBorder: 'hover:border-pink-300' },
    { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', hover: 'hover:bg-indigo-100', hoverBorder: 'hover:border-indigo-300' },
    { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', hover: 'hover:bg-teal-100', hoverBorder: 'hover:border-teal-300' },
    { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', hover: 'hover:bg-amber-100', hoverBorder: 'hover:border-amber-300' }
];

/**
 * Get consistent color for a tag
 * @param {string} tag - The tag text
 * @returns {Object} Color object with bg, text, border, hover, and hoverBorder properties
 */
export const getTagColor = (tag) => {
    if (!tag) return fallbackColors[0];
    
    // Normalize tag text for consistent matching
    const normalizedTag = tag.toLowerCase().trim().replace(/[^a-z0-9]/g, '_');
    
    // Check if we have a semantic color for this tag
    if (semanticColors[normalizedTag]) {
        return semanticColors[normalizedTag];
    }
    
    // Use fallback color based on tag hash for consistency
    const hash = normalizedTag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colorIndex = hash % fallbackColors.length;
    return fallbackColors[colorIndex];
};

/**
 * Get all available semantic colors (for reference)
 * @returns {Object} All semantic color mappings
 */
export const getAllSemanticColors = () => semanticColors;

/**
 * Get all fallback colors (for reference)
 * @returns {Array} All fallback colors
 */
export const getAllFallbackColors = () => fallbackColors;
